/**
 * Network Status API Service
 * Handles fetching network status from all exchanges with request deduplication
 * 
 * This service calls exchange APIs directly where CORS is allowed.
 * Many exchanges (Binance, Upbit, Bitget, KuCoin) have CORS headers configured.
 * For exchanges that don't allow CORS, a backend proxy would be needed.
 */

import type { NetworkStatusResponse } from '../types/networkStatus';
import { ALL_EXCHANGE_INDICES } from '../types/networkStatus';
import { EXCHANGE_API_CONFIGS } from './exchangeNetworkApis';

class NetworkStatusService {
  private inFlightRequests: Map<number, Promise<NetworkStatusResponse | null>>;

  constructor() {
    this.inFlightRequests = new Map();
  }

  /**
   * Fetch network status for a specific exchange
   * Implements request deduplication to prevent multiple concurrent requests to the same endpoint
   */
  async fetchExchangeNetworkStatus(exchange: number): Promise<NetworkStatusResponse | null> {
    // Check if there's already an in-flight request for this exchange
    if (this.inFlightRequests.has(exchange)) {
      console.log(`Request already in flight for exchange ${exchange}, returning existing promise`);
      return this.inFlightRequests.get(exchange)!;
    }

    // Create new request promise
    const requestPromise = this.performFetch(exchange);
    
    // Store in-flight request
    this.inFlightRequests.set(exchange, requestPromise);

    // Clean up after request completes
    requestPromise.finally(() => {
      this.inFlightRequests.delete(exchange);
    });

    return requestPromise;
  }

  /**
   * Perform the actual fetch operation
   * Calls exchange APIs directly where CORS allows
   */
  private async performFetch(exchange: number): Promise<NetworkStatusResponse | null> {
    try {
      const config = EXCHANGE_API_CONFIGS[exchange];
      
      if (!config) {
        console.error(`No API configuration found for ${exchange}`);
        return null;
      }

      // For exchanges that require auth or don't allow CORS, we need a backend proxy
      if (!config.corsAllowed || config.requiresAuth) {
        console.warn(`${exchange} requires backend proxy (CORS: ${config.corsAllowed}, Auth: ${config.requiresAuth})`);
        // For now, return null. In production, you'd call your backend proxy here
        return null;
      }

      console.log(`Fetching network status for ${exchange} from ${config.url}`);

      // Build the URL with timestamp if needed
      let url = config.url;
      if (exchange === 1) { // Bithumb
        url = `https://gw.bithumb.com/exchange/v1/coin-inout/info?_=${Date.now()}&retry=0`;
      } else if (exchange === 7) { // OKX
        url = `https://www.okx.com/v2/asset/currency/status?page=1&t=${Date.now()}`;
      }

      const response = await fetch(url, {
        method: config.method,
        headers: config.headers,
        signal: AbortSignal.timeout(30000), // 30 second timeout
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const rawData = await response.json();
      // Handle async parseResponse functions (like Upbit's)
      const parsedCryptos = await Promise.resolve(config.parseResponse(rawData));
      
      // Validate that parsedCryptos is an array
      if (!Array.isArray(parsedCryptos)) {
        console.error(`Exchange ${exchange} parser did not return an array:`, parsedCryptos);
        return {
          exchange,
          cryptos: [],
          timestamp: Date.now(),
        };
      }
      
      // Transform the response to our expected format
      return {
        exchange,
        cryptos: parsedCryptos,
        timestamp: Date.now(),
      };
    } catch (error) {
      console.error(`Failed to fetch network status for ${exchange}:`, error);
      return null;
    }
  }

  /**
   * Fetch network status for all exchanges in parallel
   */
  async fetchAllExchanges(): Promise<Map<number, NetworkStatusResponse | null>> {
    console.log('Fetching network status for all exchanges in parallel');
    
    // Create promises for all exchanges
    const promises = ALL_EXCHANGE_INDICES.map(async (exchange) => {
      const result = await this.fetchExchangeNetworkStatus(exchange);
      return { exchange, result };
    });

    // Wait for all requests to complete
    const results = await Promise.allSettled(promises);
    
    // Convert to Map
    const resultMap = new Map<number, NetworkStatusResponse | null>();
    
    results.forEach((result, index) => {
      const exchange = ALL_EXCHANGE_INDICES[index];
      if (result.status === 'fulfilled') {
        resultMap.set(exchange, result.value.result);
      } else {
        console.error(`Failed to fetch exchange ${exchange}:`, result.reason);
        resultMap.set(exchange, null);
      }
    });

    return resultMap;
  }

  /**
   * Fetch network status for multiple specific exchanges
   */
  async fetchExchanges(exchanges: number[]): Promise<Map<number, NetworkStatusResponse | null>> {
    console.log(`Fetching network status for ${exchanges.length} exchanges`);
    
    const promises = exchanges.map(async (exchange) => {
      const result = await this.fetchExchangeNetworkStatus(exchange);
      return { exchange, result };
    });

    const results = await Promise.allSettled(promises);
    
    const resultMap = new Map<number, NetworkStatusResponse | null>();
    
    results.forEach((result, index) => {
      const exchange = exchanges[index];
      if (result.status === 'fulfilled') {
        resultMap.set(exchange, result.value.result);
      } else {
        console.error(`Failed to fetch exchange ${exchange}:`, result.reason);
        resultMap.set(exchange, null);
      }
    });

    return resultMap;
  }

  /**
   * Clear all in-flight requests (useful for cleanup)
   */
  clearInFlightRequests(): void {
    this.inFlightRequests.clear();
  }

  /**
   * Check if there are any pending requests
   */
  hasPendingRequests(): boolean {
    return this.inFlightRequests.size > 0;
  }

  /**
   * Get the number of pending requests
   */
  getPendingRequestCount(): number {
    return this.inFlightRequests.size;
  }
}

// Export singleton instance
export const networkStatusService = new NetworkStatusService();